import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { Budget, FundingSourceTypes } from '@core/typings/budget.typing';
import { BudgetService } from '@features/budgets/budget.service';
import { ALL_SKIP_FILTER, BulkOptions, TopLevelFilter } from '@yourcause/common';
import { I18nService } from '@yourcause/common/i18n';
import { ArrayHelpersService } from '@yourcause/common/utils';
import { cloneDeep, isEqual } from 'lodash';
import { Subscription } from 'rxjs';

@Component({
    selector: 'gc-cycle-budget-selector',
    templateUrl: './cycle-budget-selector.component.html',
    styleUrls: ['./cycle-budget-selector.component.scss'],
    standalone: false
})
export class CycleBudgetSelectorComponent implements OnInit, OnChanges, OnDestroy {
  @Input() budgetIds: number[];
  @Input() isIndividual = false;
  @Output() onBudgetsChanged = new EventEmitter<number[]>();

  budgetRows: Budget[];
  bulkOptions: BulkOptions<Budget> = {
    recordIdentifierAttribute: 'id',
    onlyForTrackingRows: true,
    retainBulkSelectedItems: true,
    bulkActions: [],
    addBulkFilterToOnlyShowSelected: true
  };
  bulkSelected: Budget[];
  topLevelFilters: TopLevelFilter[] = [];
  showRequiredAlert = false;
  isAfterInit = false;
  budgetTagsMap = this.budgetService.budgetTagsMap;
  FundingSourceTypes = FundingSourceTypes;
  sub = new Subscription();

  constructor (
    private budgetService: BudgetService,
    private arrayHelper: ArrayHelpersService,
    private i18n: I18nService
  ) {
    this.sub.add(this.budgetService.changesTo$('budgets').subscribe(() => {
      if (this.isAfterInit && !!this.budgetService.budgets) {
        this.setBudgetRows();
      }
    }));
  }

  get simpleBudgetMap () {
    return this.budgetService.simpleBudgetMap;
  }

  ngOnInit () {
    this.setBudgetRows();
    this.setInitialBulkSelected();
    this.setTopLevelFilters();
    this.isAfterInit = true;
  }

  ngOnChanges (changes: SimpleChanges) {
    if (this.isAfterInit) {
      if (changes.budgetIds) {
        const bulkSelectedIds = this.arrayHelper.sort(this.bulkSelected.map((budget) => budget.id));
        const budgetIds = this.arrayHelper.sort(this.budgetIds);
        if (!isEqual(bulkSelectedIds, budgetIds)) {
          this.setInitialBulkSelected();
        }
      }
    }
  }

  setTopLevelFilters () {
    this.topLevelFilters = [
      new TopLevelFilter(
        'text',
        'name',
        '',
        this.i18n.translate('BUDGET:textSearchForABudget', {}, 'Search for a budget')
      ),
      new TopLevelFilter(
        'typeaheadSingleEquals',
        'fundingSourceType',
        ALL_SKIP_FILTER,
        '',
        {
          selectOptions: [{
            label: this.i18n.translate(
              'common:textAllTypes',
              {},
              'All types'
            ),
            value: ALL_SKIP_FILTER
          }, {
            label: this.i18n.translate(
              'GLOBAL:textCash',
              {},
              'Cash'
            ),
            value: FundingSourceTypes.DOLLARS
          }, {
            label: this.i18n.translate(
              'GLOBAL:textInKind',
              {},
              'In-Kind'
            ),
            value: FundingSourceTypes.UNITS
          }],
          customFlexBasis: '25%'
        }
      ),
      new TopLevelFilter(
        'typeaheadSingleEquals',
        'isClosed',
        ALL_SKIP_FILTER,
        null,
        {
          selectOptions: [{
            label: this.i18n.translate('CONFIG:textAllBudgets', {}, 'All budgets'),
            value: ALL_SKIP_FILTER
          }, {
            label: this.i18n.translate('common:textOpenBudgets', {}, 'Open budgets'),
            value: false
          }, {
            label: this.i18n.translate('common:textClosedBudgets', {}, 'Closed budgets'),
            value: true
          }],
          customFlexBasis: '25%'
        },
        this.i18n.translate('BUDGET:textIncludeClosedBudgets', {}, 'Include closed budgets')
      )
    ];
  }

  setBudgetRows () {
    this.budgetRows = cloneDeep(this.budgetService.budgets);
  }

  setInitialBulkSelected () {
    const bulkSelected: Budget[] = [];
    this.budgetIds.forEach((budgetId) => {
      const found = this.budgetService.budgets.find((budget) => {
        return budgetId === budget.id;
      });
      if (!!found) {
        bulkSelected.push(found);
      }
    });
    this.bulkSelected = bulkSelected;
  }

  bulkSelectedChanged (budgets: Budget[]) {
    this.bulkSelected = budgets;
    const budgetIds = budgets.map((budget) => budget.id);
    this.onBudgetsChanged.emit(budgetIds);
  }

  ngOnDestroy () {
    this.sub.unsubscribe();
  }
}
